import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './PastEventsGallery.css';

const PastEventsGallery = ({
  images,
  onImageClick,
  className = '',
  ...rest
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
    if (onImageClick) {
      onImageClick(index);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const moveToPrev = () => {
    setCurrentImage((currentImage + images.length - 1) % images.length);
  };

  const moveToNext = () => {
    setCurrentImage((currentImage + 1) % images.length);
  };

  // Handle keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') moveToPrev();
      if (e.key === 'ArrowRight') moveToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentImage]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);

  // Debug log
  // console.log('PastEventsGallery - Number of images:', images.length);

  return (
    <section className={`past-events-gallery ${className}`.trim()} {...rest}>
      <h2 className="past-events-gallery__title">Past Events Gallery</h2>
      
      {images.length === 0 ? (
        <p className="past-events-gallery__empty">Images will be available soon.</p>
      ) : (
        <>
          <div className="past-events-gallery__grid">
            {images.map((image, index) => (
              <div
                key={image.image_id || image.id || index}
                className="past-events-gallery__item"
                onClick={() => openLightbox(index)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    openLightbox(index);
                  }
                }}
              >
                <img
                  src={image.image_url || image.image_name || image.url}
                  alt={image.title || image.alt || `Gallery image ${index + 1}`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {lightboxOpen && (
            <div className="lightbox-overlay" onClick={closeLightbox}>
              <button
                className="lightbox-close"
                onClick={closeLightbox}
                aria-label="Close lightbox"
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>

              <button
                className="lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  moveToPrev();
                }}
                aria-label="Previous image"
              >
                <i className="fas fa-chevron-left" aria-hidden="true"></i>
              </button>

              <button
                className="lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  moveToNext();
                }}
                aria-label="Next image"
              >
                <i className="fas fa-chevron-right" aria-hidden="true"></i>
              </button>

              <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                <img
                  src={images[currentImage].image_url || images[currentImage].image_name || images[currentImage].url}
                  alt={images[currentImage].title || images[currentImage].alt || `Gallery image ${currentImage + 1}`}
                  className="lightbox-image"
                />
                {/* Caption intentionally removed — no text under lightbox image */}
              </div>

              <div className="lightbox-counter">
                {currentImage + 1} / {images.length}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

PastEventsGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      image_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      image_url: PropTypes.string,
      title: PropTypes.string,
    })
  ).isRequired,
  onImageClick: PropTypes.func,
  className: PropTypes.string,
};

PastEventsGallery.defaultProps = {
  onImageClick: undefined,
  className: '',
};

export default PastEventsGallery;

